import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MapPin, WhatsappLogo } from "@phosphor-icons/react";
import { countriesData } from "../../constant/countries";
import "./OrderDetailsModal.css";

const emptyDetails = {
  senderName: "", anonymous: false, receiverName: "", receiverPhone: "", receiverWhatsapp: "",
  phoneCountry: "IN", whatsappCountry: "IN", office: "", floor: "", tower: "",
  homeOrOffice: "1", message: "",
};

const OrderDetailsModal = ({ show, address, senderName = "", countryCode = "IN", initialAddress = {},
  submitting = false, error = "", onClose, onSubmit }) => {
  const [details, setDetails] = useState(emptyDetails);
  const [errors, setErrors] = useState({});
  const dialogRef = useRef(null);
  const titleId = useId();
  const errorId = useId();
  const initialOffice = initialAddress?.building || "";
  const initialFloor = initialAddress?.floor || "";
  const initialTower = initialAddress?.tower || initialAddress?.wing || "";

  useEffect(() => {
    if (!show) return;
    const country = countriesData.some((item) => item.country_code === countryCode) ? countryCode : "IN";
    setDetails({ ...emptyDetails, senderName, phoneCountry: country, whatsappCountry: country,
      office: initialOffice, floor: initialFloor, tower: initialTower });
    setErrors({});
  }, [show, address, senderName, countryCode, initialOffice, initialFloor, initialTower]);

  useEffect(() => {
    if (!show) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    dialogRef.current?.querySelector("input")?.focus();
    const keyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!submitting) onClose();
      }
      if (event.key === "Tab") {
        const controls = [...dialogRef.current.querySelectorAll("input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)")];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!controls.includes(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", keyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.removeEventListener("keydown", keyDown, true);
      previousFocus?.focus();
    };
  }, [show, onClose, submitting]);

  const update = (key, value) => {
    setDetails((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const submit = (event) => {
    event.preventDefault();
    if (submitting) return;
    const next = {};
    if (!details.anonymous && !details.senderName.trim()) next.senderName = "Enter the sender's name or select Send Anonymous.";
    if (!details.receiverName.trim()) next.receiverName = "Enter the receiver's name.";
    if (!/^\d{7,15}$/.test(details.receiverPhone)) next.receiverPhone = "Enter a valid receiver phone number.";
    if (details.receiverWhatsapp && !/^\d{7,15}$/.test(details.receiverWhatsapp)) next.receiverWhatsapp = "Enter a valid WhatsApp number.";
    if (!details.tower.trim()) next.tower = "Enter the tower or wing.";
    setErrors(next);
    if (Object.keys(next).length) {
      dialogRef.current?.querySelector(`[name="${Object.keys(next)[0]}"]`)?.focus();
      return;
    }
    onSubmit({ ...details, senderName: details.anonymous ? "" : details.senderName.trim(),
      receiverName: details.receiverName.trim(), office: details.office.trim(), floor: details.floor.trim(), tower: details.tower.trim(),
      phoneCode: countriesData.find((item) => item.country_code === details.phoneCountry)?.phonecode || "",
      whatsappCode: countriesData.find((item) => item.country_code === details.whatsappCountry)?.phonecode || "",
    });
  };

  const textField = (key, placeholder, required = false) => (
    <div className="order-details-field">
      <input name={key} aria-label={placeholder.replace(/\s*\*$/, "")} aria-invalid={Boolean(errors[key])}
        aria-describedby={errors[key] ? `${errorId}-${key}` : undefined} placeholder={placeholder}
        value={details[key]} required={required} disabled={key === "senderName" && details.anonymous}
        onChange={(event) => update(key, event.target.value)} maxLength={150} />
      {errors[key] && <span className="order-details-field__error" id={`${errorId}-${key}`}>{errors[key]}</span>}
    </div>
  );

  const phoneField = (key, countryKey, label, optional = false) => (
    <div className="order-details-field">
      <div className="order-details-phone">
        <select aria-label={`${label} country code`} value={details[countryKey]} onChange={(event) => update(countryKey, event.target.value)}>
          {countriesData.map((country) => <option key={country.country_code} value={country.country_code}>
            {country.flagEmoji} {country.phonecode}
          </option>)}
        </select>
        <input type="tel" inputMode="tel" name={key} aria-label={label} placeholder={label}
          aria-invalid={Boolean(errors[key])} aria-describedby={errors[key] ? `${errorId}-${key}` : undefined}
          value={details[key]} required={!optional} maxLength={15}
          onChange={(event) => update(key, event.target.value.replace(/\D/g, ""))} />
        {optional && <WhatsappLogo className="order-details-whatsapp" size={23} weight="fill" aria-hidden="true" />}
      </div>
      {errors[key] && <span className="order-details-field__error" id={`${errorId}-${key}`}>{errors[key]}</span>}
    </div>
  );

  if (!show) return null;
  return createPortal(
    <div className="order-details-overlay">
      <form ref={dialogRef} className="order-details-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} noValidate onSubmit={submit}>
        <h2 id={titleId}>Enter Details</h2>
        <div className="order-details-address"><MapPin size={22} weight="fill" aria-hidden="true" /><span>{address}</span></div>
        <div className="order-details-body">
          <fieldset disabled={submitting}>
            {textField("senderName", "Sender Name", !details.anonymous)}
            <label className="order-details-anonymous"><input type="checkbox" checked={details.anonymous}
              onChange={(event) => { update("anonymous", event.target.checked); setErrors((current) => ({ ...current, senderName: "" })); }} />Send Anonymous</label>
            <h3>Receiver Details</h3>
            {textField("receiverName", "Receiver Name", true)}
            {phoneField("receiverPhone", "phoneCountry", "Receiver Phone Number")}
            {phoneField("receiverWhatsapp", "whatsappCountry", "Receiver WhatsApp Number", true)}
            <div className="order-details-address-type" role="group" aria-label="Delivery address type">
              {[{ value: "1", label: "Home" }, { value: "2", label: "Office" }].map((option) => (
                <label key={option.value}><input type="radio" name="homeOrOffice" value={option.value}
                  checked={details.homeOrOffice === option.value} onChange={() => update("homeOrOffice", option.value)} />{option.label}</label>
              ))}
            </div>
            {textField("office", "Office Name / Number")}
            {textField("floor", "Floor")}
            {textField("tower", "Tower / Wing *", true)}
            <div className="order-details-field"><textarea name="message" aria-label="Message (optional)" placeholder="Message (optional)"
              rows={2} maxLength={1000} value={details.message} onChange={(event) => update("message", event.target.value)} /></div>
          </fieldset>
        </div>
        <div className="order-details-footer">
          {error && <p role="alert">{error}</p>}
          <div><button type="button" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" disabled={submitting}>{submitting ? "Placing order..." : "Place Order"}</button></div>
        </div>
      </form>
    </div>, document.body
  );
};

export default OrderDetailsModal;
